import { useEffect, useRef, useState } from "react";
import {
    Stack,
    Spinner,
    DefaultButton,
    PrimaryButton,
    Label,
    Text,
    MessageBar,
    MessageBarType,
    ProgressIndicator
} from "@fluentui/react";
import { useBeforeUnloadGuard } from "../../../hooks/useBeforeUnloadGuard";
import { updateContainerTags, updateFileTags } from "../../../api/admin/api_admin";
import { displayFileName } from "../components";
import {
    ApplyModalFrame,
    ApplyProgressRow,
    ApplyResultMessageBar,
    IndexerRunOutcome,
    IndexingPane,
    runIndexerForApply,
    runSequentially
} from "./apply_common";
import { TagDefinitionChange, TagFileChange } from "./tag_changes";

export interface ApplyTagsResult {
    // 実行に入らずキャンセルされた場合はfalse。この場合は編集内容をそのまま残す
    executed: boolean
    // サーバ側の状態が変わったため、一覧の再取得が必要か
    reloadRequired: boolean
    // インデクサの実行(＝検索への反映)まで到達したか
    indexerStarted: boolean
    message: string
    // サブカテゴリ定義の反映に失敗したか。失敗時は編集内容を復元してやり直せるようにする
    definitionFailed: boolean
    // 反映できなかったファイル。呼び出し側で編集内容へ戻し、やり直せるようにする
    failedFiles: TagFileChange[]
}

/**
 * 保留していたサブカテゴリの変更を
 * 「確認 → 定義の反映 → ファイルの反映 → 検索へ反映」の順にまとめて実行する。
 *
 * 定義の反映を先に行うのは、PUT /api/tags/container/<cnt>/file/<fil> が
 * コンテナタグに存在しないキーを400で弾くため。新規サブカテゴリを先に登録しておく必要がある。
 */
type Phase = "confirm" | "definition" | "files" | "indexing" | "done"

export const ApplyTagsModal = (props: {
    containerSeq: number
    containerName: string
    // サブカテゴリ定義の変更。変更が無ければnull
    definitionChange: TagDefinitionChange | null
    fileChanges: TagFileChange[]
    // タグキーから表示名を引くための対応表(編集中の定義)
    tagValueByKey: Map<string, string>
    onFinished: (result: ApplyTagsResult) => void
}) => {
    const { containerSeq, containerName, definitionChange, fileChanges, tagValueByKey, onFinished } = props

    const [phase, setPhase] = useState<Phase>("confirm")
    // 定義の反映に失敗した場合のメッセージ。失敗したらファイルの反映へは進まない
    const [definitionError, setDefinitionError] = useState<string | null>(null)
    // ファイル単位の反映結果。件数が進捗の分子になる
    const [fileResults, setFileResults] = useState<Map<number, boolean>>(new Map())
    const [failedFiles, setFailedFiles] = useState<TagFileChange[]>([])
    const [filesDone, setFilesDone] = useState(false)
    const [indexerOutcome, setIndexerOutcome] = useState<IndexerRunOutcome | null>(null)

    // 実行中はタブを閉じられると処理が中断されるため、離脱前に確認する
    const isRunning = phase === "definition" || phase === "files" || phase === "indexing"
    useBeforeUnloadGuard(isRunning)

    const tagLabelOf = (tagKeys: string[]): string => {
        const label = tagKeys.map(k => tagValueByKey.get(k)).filter((v): v is string => Boolean(v)).join('、')
        return label === "" ? "（なし）" : label
    }

    const startApply = () => {
        if (definitionChange !== null) {
            setPhase("definition")
        } else if (0 < fileChanges.length) {
            setPhase("files")
        } else {
            runIndexerPhase()
        }
    }

    // 定義の反映。失敗した場合は、存在しないキーでファイル側が弾かれるためここで打ち切る
    const runDefinitionPhase = async () => {
        if (definitionChange === null) return
        try {
            const result = await updateContainerTags({ seq: containerSeq, tags: definitionChange.tags })
            if (!result.success) {
                setDefinitionError(result.message ?? "サブカテゴリの更新に失敗しました")
                setPhase("done")
                return
            }
        } catch (err) {
            setDefinitionError(err instanceof Error ? err.message : "サブカテゴリの更新に失敗しました")
            setPhase("done")
            return
        }

        if (0 < fileChanges.length) {
            setPhase("files")
        } else {
            runIndexerPhase()
        }
    }

    const runFilesPhase = async () => {
        const { failed } = await runSequentially(
            fileChanges,
            change => change.file.seq,
            async change => (await updateFileTags({
                cntSeq: containerSeq,
                filSeq: change.file.seq,
                tagKeys: change.tagKeys,
            })).success,
            setFileResults
        )
        setFailedFiles(failed)
        setFilesDone(true)

        // 失敗があった場合はどのファイルが残ったかを見せるため、次へ進むのはユーザーに任せる
        if (failed.length === 0) {
            runIndexerPhase()
        }
    }

    const runIndexerPhase = async () => {
        setPhase("indexing")
        setIndexerOutcome(await runIndexerForApply(containerSeq))
        setPhase("done")
    }

    // React.StrictMode下ではeffectが2回実行されるため、二重に実行しないよう番人を置く
    const startedPhaseRef = useRef<Phase | null>(null)
    useEffect(() => {
        if (startedPhaseRef.current === phase) return
        if (phase === "definition") {
            startedPhaseRef.current = phase
            runDefinitionPhase()
        } else if (phase === "files") {
            startedPhaseRef.current = phase
            runFilesPhase()
        }
    }, [phase])

    const appliedFileCount = [...fileResults.values()].filter(Boolean).length
    // 確認画面まででキャンセルした場合を除き、サーバ側が変わっている可能性がある
    const reloadRequired = phase !== "confirm" && definitionError === null

    const finish = () => onFinished({
        executed: true,
        reloadRequired,
        indexerStarted: indexerOutcome?.succeeded ?? false,
        message: definitionError ?? indexerOutcome?.message ?? "",
        definitionFailed: definitionError !== null,
        failedFiles,
    })

    return (
        <ApplyModalFrame containerName={containerName}>
            {phase === "confirm" && (
                <Stack tokens={{ childrenGap: 16 }} styles={{ root: { flex: 1, minHeight: 0 } }}>
                    <MessageBar messageBarType={MessageBarType.severeWarning}>
                        以下の変更を実行し、検索へ反映します。
                        削除したサブカテゴリは、付与されていたファイルからも取り除かれます。
                    </MessageBar>

                    <Stack style={{ overflow: 'auto', flex: 1 }} tokens={{ childrenGap: 16 }}>
                        <Stack tokens={{ childrenGap: 4 }}>
                            <Label>サブカテゴリの定義</Label>
                            {definitionChange === null ? (
                                <Text variant="small">変更なし</Text>
                            ) : (
                                <>
                                    {definitionChange.added.map(value => (
                                        <Text key={`add-${value}`} variant="small">・追加: {value}</Text>
                                    ))}
                                    {definitionChange.renamed.map(r => (
                                        <Text key={`ren-${r.from}`} variant="small">・名前の変更: {r.from} → {r.to}</Text>
                                    ))}
                                    {definitionChange.removed.map(value => (
                                        <Text key={`del-${value}`} variant="small">・削除: {value}</Text>
                                    ))}
                                </>
                            )}
                        </Stack>

                        <Stack tokens={{ childrenGap: 4 }}>
                            <Label>ファイルへの付与（{fileChanges.length}件）</Label>
                            {fileChanges.length === 0 ? (
                                <Text variant="small">変更なし</Text>
                            ) : (
                                fileChanges.map(change => (
                                    <Text key={change.file.seq} variant="small">
                                        ・{displayFileName(change.file)} → {tagLabelOf(change.tagKeys)}
                                    </Text>
                                ))
                            )}
                        </Stack>
                    </Stack>

                    <Stack horizontalAlign="end" horizontal tokens={{ childrenGap: 10 }}>
                        <DefaultButton
                            text="キャンセル"
                            iconProps={{ iconName: "Cancel" }}
                            onClick={() => onFinished({
                                executed: false,
                                reloadRequired: false,
                                indexerStarted: false,
                                message: "",
                                definitionFailed: false,
                                failedFiles: [],
                            })}
                        />
                        <PrimaryButton text="実行する" onClick={startApply} />
                    </Stack>
                </Stack>
            )}

            {phase === "definition" && (
                <Stack horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 10 }} styles={{ root: { flex: 1 } }}>
                    <Spinner label="サブカテゴリの定義を反映しています..." />
                    <Text variant="small">
                        削除したサブカテゴリがある場合、付与済みファイルの件数によっては時間がかかります。
                    </Text>
                </Stack>
            )}

            {phase === "files" && (
                <Stack tokens={{ childrenGap: 16 }} styles={{ root: { flex: 1, minHeight: 0 } }}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                        {!filesDone && <Spinner />}
                        <Label styles={{ root: { fontSize: 14, fontWeight: 700 } }}>
                            ファイルへの付与（{fileChanges.length}件）
                        </Label>
                    </Stack>

                    {filesDone && 0 < failedFiles.length && (
                        <MessageBar messageBarType={MessageBarType.error}>
                            {failedFiles.length}件のファイルを更新できませんでした。
                            エラー表示のファイルは変更されていません。
                        </MessageBar>
                    )}

                    <Stack style={{ overflow: 'auto', flex: 1 }} tokens={{ childrenGap: 6 }}>
                        {fileChanges.map(change => (
                            <ApplyProgressRow
                                key={change.file.seq}
                                name={displayFileName(change.file)}
                                detail={tagLabelOf(change.tagKeys)}
                                result={fileResults.get(change.file.seq)}
                            />
                        ))}
                    </Stack>

                    <ProgressIndicator
                        label={filesDone ? "反映完了" : "反映中..."}
                        description={`${fileResults.size} / ${fileChanges.length}件`}
                        percentComplete={0 < fileChanges.length ? fileResults.size / fileChanges.length : 1}
                    />

                    {filesDone && 0 < failedFiles.length && (
                        <Stack horizontalAlign="end" horizontal tokens={{ childrenGap: 10 }}>
                            <PrimaryButton text="続行する" onClick={runIndexerPhase} />
                        </Stack>
                    )}
                </Stack>
            )}

            {phase === "indexing" && <IndexingPane />}

            {phase === "done" && (
                <Stack tokens={{ childrenGap: 16 }} styles={{ root: { flex: 1, minHeight: 0 } }}>
                    <ApplyResultMessageBar outcome={indexerOutcome} errorMessage={definitionError} />

                    <Stack tokens={{ childrenGap: 4 }} styles={{ root: { flex: 1 } }}>
                        {definitionChange !== null && (
                            <Text>
                                サブカテゴリの定義: {definitionError === null ? "反映しました" : "反映できませんでした"}
                            </Text>
                        )}
                        {0 < fileChanges.length && definitionError === null && (
                            <Text>
                                ファイルへの付与: {appliedFileCount}件
                                {0 < failedFiles.length ? `（${failedFiles.length}件失敗）` : ""}
                            </Text>
                        )}
                    </Stack>

                    <Stack horizontalAlign="end" horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton text="閉じる" onClick={finish} />
                    </Stack>
                </Stack>
            )}
        </ApplyModalFrame>
    )
}
